<template>
  <div class="info-page">
    <div class="info-hero">
      <div class="info-hero__inner">
        <p class="info-eyebrow">Legal</p>
        <h1 class="info-h1">Terms &amp;<br><em>Conditions</em></h1>
        <p class="info-lead">
          Please read these terms carefully before using Maths Eleven Plus.
          Last updated: <strong>1 January 2025</strong>.
        </p>
      </div>
    </div>

    <div class="info-body legal-body">

      <div class="legal-toc">
        <h3 class="legal-toc__title">Contents</h3>
        <ol class="legal-toc__list">
          <li><a href="#acceptance">1. Acceptance of Terms</a></li>
          <li><a href="#service">2. Description of Service</a></li>
          <li><a href="#accounts">3. Accounts &amp; Registration</a></li>
          <li><a href="#subscriptions">4. Subscriptions &amp; Billing</a></li>
          <li><a href="#obligations">5. User Obligations</a></li>
          <li><a href="#ip">6. Intellectual Property</a></li>
          <li><a href="#data">7. Data &amp; Privacy</a></li>
          <li><a href="#liability">8. Limitation of Liability</a></li>
          <li><a href="#termination">9. Termination</a></li>
          <li><a href="#governing">10. Governing Law</a></li>
          <li><a href="#changes">11. Changes to These Terms</a></li>
          <li><a href="#contact">12. Contact</a></li>
        </ol>
      </div>

      <section id="acceptance" class="legal-section">
        <h2 class="legal-h2">1. Acceptance of Terms</h2>
        <p>By accessing or using Maths Eleven Plus (the "Service"), you agree to be bound by these Terms and Conditions and our Privacy Policy. If you do not agree, please do not use the Service.</p>
        <p>These Terms apply to all users, including parents or guardians who register accounts and children who use the learning platform under a parent or guardian's account.</p>
      </section>

      <section id="service" class="legal-section">
        <h2 class="legal-h2">2. Description of Service</h2>
        <p>Maths Eleven Plus is an adaptive online learning platform providing practice questions, progress tracking, and curriculum-aligned content for the UK 11+ Grammar School entrance examination. The Service is provided by Maths Eleven Plus Ltd ("we", "us", or "our"), a company registered in England and Wales.</p>
        <p>We reserve the right to modify, suspend, or discontinue any part of the Service at any time, with or without notice. We will not be liable to you or any third party for any modification, suspension, or discontinuation.</p>
      </section>

      <section id="accounts" class="legal-section">
        <h2 class="legal-h2">3. Accounts &amp; Registration</h2>
        <p>You must be at least 18 years old to create an account. If you are a parent or guardian, you may create an account for your child to use under your supervision. You are responsible for all activity on your account.</p>
        <p>You agree to provide accurate and complete information when registering and to keep your login credentials secure. You must notify us immediately at contact@mathselevenplus.co.uk if you suspect unauthorised access to your account.</p>
        <p>We reserve the right to suspend or terminate accounts that violate these Terms, including accounts that provide false information or misuse the Service.</p>
      </section>

      <section id="subscriptions" class="legal-section">
        <h2 class="legal-h2">4. Subscriptions &amp; Billing</h2>
        <p>Some features of the Service require a paid subscription (Pro or Family plan). Subscription fees are charged in advance on a monthly basis. All prices are in GBP and inclusive of applicable VAT.</p>
        <p>You may cancel your subscription at any time from your account dashboard. Cancellation takes effect at the end of the current billing period; no partial refunds are issued for unused time unless required by law.</p>
        <p>We reserve the right to change subscription prices on 30 days' notice. Continued use after a price change constitutes acceptance of the new pricing.</p>
        <p>Payments are processed securely by Stripe. We do not store payment card details.</p>
      </section>

      <section id="obligations" class="legal-section">
        <h2 class="legal-h2">5. User Obligations</h2>
        <p>You agree not to:</p>
        <ul class="legal-list">
          <li>Use the Service for any unlawful purpose or in violation of any applicable laws;</li>
          <li>Share your account credentials with third parties or allow others to use your account;</li>
          <li>Attempt to reverse-engineer, scrape, or copy any content from the Service;</li>
          <li>Use automated tools (bots, scripts) to interact with the Service;</li>
          <li>Post, upload, or transmit any content that is harmful, offensive, or infringes third-party rights;</li>
          <li>Interfere with or disrupt the Service or its underlying infrastructure.</li>
        </ul>
      </section>

      <section id="ip" class="legal-section">
        <h2 class="legal-h2">6. Intellectual Property</h2>
        <p>All content on the Service, including but not limited to questions, explanations, curriculum materials, software, and design, is the property of Maths Eleven Plus Ltd or its licensors and is protected by copyright and other intellectual property laws.</p>
        <p>We grant you a limited, non-exclusive, non-transferable licence to access and use the Service for personal, non-commercial purposes. This licence does not permit you to reproduce, distribute, or create derivative works from any content without our prior written permission.</p>
        <p>Your child's practice data and progress information remain yours. We claim no ownership over personal data submitted through your use of the Service.</p>
      </section>

      <section id="data" class="legal-section">
        <h2 class="legal-h2">7. Data &amp; Privacy</h2>
        <p>We process personal data in accordance with our Privacy Policy and applicable UK data protection law (UK GDPR and the Data Protection Act 2018). By using the Service, you consent to such processing.</p>
        <p>We do not use children's data for advertising purposes, and we do not share data with third parties except as strictly necessary to operate the Service (e.g., payment processing, cloud hosting).</p>
      </section>

      <section id="liability" class="legal-section">
        <h2 class="legal-h2">8. Limitation of Liability</h2>
        <p>The Service is provided on an "as is" and "as available" basis. We make no warranties, express or implied, regarding the accuracy, completeness, or fitness for any particular purpose of the Service or its content.</p>
        <p>To the fullest extent permitted by law, Maths Eleven Plus Ltd shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of, or inability to use, the Service.</p>
        <p>Our total liability to you for any claim arising from these Terms or your use of the Service shall not exceed the amount you paid to us in the 12 months preceding the claim, or £100, whichever is greater.</p>
        <p>Nothing in these Terms excludes or limits our liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by English law.</p>
      </section>

      <section id="termination" class="legal-section">
        <h2 class="legal-h2">9. Termination</h2>
        <p>You may terminate your account at any time by contacting contact@mathselevenplus.co.uk or through your account settings. We may terminate or suspend your access immediately, without prior notice or liability, if you breach these Terms.</p>
        <p>On termination, your right to use the Service ceases immediately. We will retain your data in accordance with our Privacy Policy.</p>
      </section>

      <section id="governing" class="legal-section">
        <h2 class="legal-h2">10. Governing Law</h2>
        <p>These Terms are governed by and construed in accordance with the laws of England and Wales. Any dispute arising from or relating to these Terms or your use of the Service shall be subject to the exclusive jurisdiction of the courts of England and Wales.</p>
      </section>

      <section id="changes" class="legal-section">
        <h2 class="legal-h2">11. Changes to These Terms</h2>
        <p>We may update these Terms from time to time. We will notify you of material changes by email or by posting a notice on the Service. Your continued use of the Service after any change constitutes your acceptance of the revised Terms.</p>
      </section>

      <section id="contact" class="legal-section">
        <h2 class="legal-h2">12. Contact</h2>
        <p>If you have any questions about these Terms, please contact us:</p>
        <address class="legal-address">
          Maths Eleven Plus Ltd<br>
          Email: <a href="mailto:legal@mathselevenplus.co.uk">legal@mathselevenplus.co.uk</a><br>
          Registered in England and Wales
        </address>
      </section>

    </div>
  </div>
</template>

<script setup>
import { usePageMeta } from '@/composables/usePageMeta';
usePageMeta({ title: 'Terms & Conditions — Maths Eleven Plus', description: 'Read the Terms and Conditions for using Maths Eleven Plus.' });
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,600;0,700;1,600;1,700&family=Jost:wght@400;500;600;700&display=swap');

.info-page { font-family: 'Jost', sans-serif; }
.info-hero { background: #0D1628; padding: 5rem 2rem 4rem; }
.info-hero__inner { max-width: 52rem; margin: 0 auto; }
.info-eyebrow { font-size: 0.8rem; font-weight: 700; letter-spacing: 0.1em; text-transform: uppercase; color: #B8902A; margin-bottom: 1rem; }
.info-h1 { font-family: 'Cormorant Garamond', serif; font-size: clamp(2.25rem, 5vw, 3.25rem); font-weight: 700; color: #fff; line-height: 1.15; margin-bottom: 1.25rem; }
.info-h1 em { color: #B8902A; font-style: italic; }
.info-lead { font-size: 1.05rem; color: rgba(255,255,255,0.65); max-width: 44rem; line-height: 1.7; }

.info-body { max-width: 52rem; margin: 0 auto; padding: 3rem 2rem 4rem; }

/* TOC */
.legal-toc { background: #fff; border: 1px solid #e5e7eb; border-left: 4px solid #B8902A; border-radius: 0.75rem; padding: 1.5rem 2rem; margin-bottom: 2.5rem; }
.legal-toc__title { font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #B8902A; margin-bottom: 0.75rem; }
.legal-toc__list { margin: 0; padding-left: 1.25rem; display: flex; flex-direction: column; gap: 0.375rem; }
.legal-toc__list li { font-size: 0.875rem; }
.legal-toc__list a { color: #374151; text-decoration: none; transition: color 0.15s; }
.legal-toc__list a:hover { color: #B8902A; }

.legal-section { margin-bottom: 2.5rem; scroll-margin-top: 80px; }
.legal-h2 { font-family: 'Cormorant Garamond', serif; font-size: 1.5rem; font-weight: 700; color: #0D1628; margin-bottom: 0.875rem; padding-bottom: 0.5rem; border-bottom: 1px solid #e5e7eb; }
.legal-section p { font-size: 0.9rem; line-height: 1.8; color: #374151; margin-bottom: 0.75rem; }
.legal-list { margin: 0.5rem 0 0.75rem 1.5rem; display: flex; flex-direction: column; gap: 0.4rem; }
.legal-list li { font-size: 0.9rem; line-height: 1.7; color: #374151; }
.legal-address { font-style: normal; font-size: 0.9rem; line-height: 1.8; color: #374151; background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 0.5rem; padding: 1rem 1.25rem; margin-top: 0.5rem; }
.legal-address a { color: #B8902A; }
</style>
